<template>
  <div class="invigilate-monitor">
    <ProctorMonitor
      ref="proctorMonitor"
      v-if="status && cameraStream"
      :stream="cameraStream"
      :template-id="template_id"
      :project-code="resolvedProjectCode"
      mode="exam"
      :log-speech="true"
      @face-status-change="$emit('faceStatusChange', $event)"
      @penalty-summary="$emit('penaltySummary', $event)"
    />
    <div v-else @click="handleReconnectClick" class="tag">
      {{
        status
          ? (screenOnly ? "Screen proctoring" : "Proctoring")
          : "Proctoring connection is interrupted, please click to reconnect."
      }}
    </div>
    <!-- 存放canvas的容器 实际页面不显示 -->
    <canvas
      id="cameraCanvas"
      ref="cameraCanvas"
      :width="cameraOptions.video.width"
      :height="cameraOptions.video.height"
      style="display: none"
    ></canvas>
    <canvas
      id="screenCanvas"
      ref="screenCanvas"
      :width="screenOptions.video.width"
      :height="screenOptions.video.height"
      style="display: none"
    ></canvas>
  </div>
</template>

<script>
import { getUserId, getProjectCode } from "../../../utils/store";
import {
  getExamEvidenceQiniuToken,
  uploadExamShotByUrl,
  changeVisibleLog,
  initExamShotTimePoint,
} from "../../../api/index";
import * as qiniu from "qiniu-js";
import ProctorMonitor from "../ProctorMonitor";

const SHOT_MIME_TYPE = "image/jpeg";
const CAMERA_SHOT_QUALITY = 0.72;
const SCREEN_SHOT_QUALITY = 0.75;
const EXAM_EVIDENCE_BASE_URL = "https://ap.learningfirst.io/";

export default {
  components: {
    ProctorMonitor,
  },
  props: {
    cameraOptions: {
      default() {
        return {
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: {
            width: 640,
            height: 480,
            aspectRatio: 4 / 3,
            facingMode: "user",
            resizeMode: "none",
          },
        };
      },
    },
    screenOptions: {
      default() {
        return {
          audio: false,
          video: { width: 1280, height: 720 },
        };
      },
    },
    value: [Boolean],
    shotInterval: {
      default: 10000,
      type: Number,
    },
    template_id: [String],
    projectCode: {
      type: String,
      default: "",
    },
    // Multimedia proctoring disabled: share the screen only, never request
    // the camera or microphone.
    screenOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cameraVideo: null, // 摄像头视频容器
      screenVideo: null, // 屏幕视频容器
      cameraStream: null, // 摄像头视频流
      screenStream: null, // 屏幕视频流
      status: this.value,
      timer: "",
      visibleShotTimer: "",
      shotTable: [],
      index: 0,
      shotTimer: null,
      shotGeneration: 0,
      initializing: false,
      mediaRequestId: 0,
      isDestroyed: false,
      windowActivityHandlersBound: false,
      pageInactive: false,
    };
  },
  computed: {
    resolvedProjectCode() {
      return String(this.projectCode || getProjectCode() || "").trim();
    },
  },
  created() {
    this.startCheckOnline(); //检查在线状态
  },
  beforeDestroy() {
    this.isDestroyed = true;
    this.mediaRequestId++;
    this.initializing = false;
    this.clearCheckOnline();
    this.clearAllResource();
  },
  methods: {
    flushPenaltyEvents() {
      const monitor = this.$refs.proctorMonitor;
      if (!monitor || typeof monitor.flushPenaltyEvents !== "function") {
        return Promise.resolve(true);
      }
      return monitor.flushPenaltyEvents();
    },
    handleReconnectClick() {
      return this.init();
    },
    isPageReadyForMediaRequest() {
      return Boolean(
        !document.hidden &&
        (!document.hasFocus || document.hasFocus())
      );
    },
    hasMediaRequestUserActivation() {
      return Boolean(
        !navigator.userActivation || navigator.userActivation.isActive
      );
    },
    getMediaRequestErrorMessage(error, mediaType) {
      const errorName = error && error.name ? error.name : "";
      if (errorName === "InvalidStateError") {
        return "Keep this exam tab visible and click Connect Proctoring again.";
      }
      if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
        return mediaType === "screen"
          ? "Screen sharing was not started. Click Connect Proctoring and choose Entire Screen."
          : "Camera access was not allowed. Enable camera access and try again.";
      }
      if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
        return mediaType === "screen"
          ? "No screen-sharing source is available."
          : "No camera was found.";
      }
      if (errorName === "NotReadableError" || errorName === "TrackStartError") {
        return mediaType === "screen"
          ? "Screen sharing could not start because the source is unavailable."
          : "The camera is already in use by another application.";
      }
      return mediaType === "screen"
        ? "Screen sharing could not be started. Please try again."
        : "The camera could not be started. Please try again.";
    },
    onblur() {
      if (!this.status || this.pageInactive) {
        return;
      }
      this.pageInactive = true;
      changeVisibleLog(getUserId(), this.resolvedProjectCode, this.template_id).then(
        () => {
          this.$emit("visibleChange");
        }
      ).catch((error) => {
        console.error("Unable to record exam visibility change", error);
      });
      this.getShot(true);
      this.setScreenVisibleShot();
    },
    onfocus() {
      if (document.hidden) {
        return;
      }
      this.pageInactive = false;
      this.clearScreenVisibleShot();
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.onblur();
      } else if (!document.hasFocus || document.hasFocus()) {
        this.onfocus();
      }
    },
    // 先执行这个方法 同时获取摄像头与屏幕流权限 获取流
    async init(options = {}) {
      const automatic = Boolean(options && options.automatic);
      if (this.status || this.initializing || this.isDestroyed) {
        return false;
      }
      if (!this.isPageReadyForMediaRequest()) {
        if (!automatic) {
          this.$message.warning(
            "Keep this exam tab visible, then click Connect Proctoring."
          );
        }
        return false;
      }
      if (!automatic && !this.hasMediaRequestUserActivation()) {
        this.$message.warning(
          "Click Connect Proctoring to open the screen-sharing picker."
        );
        return false;
      }

      this.initializing = true;
      const requestId = ++this.mediaRequestId;
      let cameraStream = null;
      let screenStream = null;
      let cameraVideo = null;
      let screenVideo = null;
      let cameraLoading = null;
      let screenLoading = null;
      let committed = false;

      this.clearScreenVisibleShot();
      this.clearAllResource();

      try {
        if (
          !navigator ||
          !navigator.mediaDevices ||
          (!this.screenOnly && !navigator.mediaDevices.getUserMedia)
        ) {
          this.$message.error("Your browser does not support camera access.");
          return false;
        }
        if (!navigator.mediaDevices.getDisplayMedia) {
          this.$message.error("Your browser does not support screen sharing.");
          return false;
        }

        if (!this.screenOnly) {
          cameraVideo = this.createCaptureVideo();
        }
        screenVideo = this.createCaptureVideo();

        // Screen capture must be requested first so a manual retry keeps the
        // browser's transient user activation and opens only one picker.
        screenLoading = this.$loading({
          lock: true,
          text: "Getting screen authorization...",
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });
        try {
          screenStream = await navigator.mediaDevices.getDisplayMedia(
            this.screenOptions
          );
        } catch (error) {
          if (this.isCurrentMediaRequest(requestId) && !automatic) {
            this.$message.error(
              this.getMediaRequestErrorMessage(error, "screen")
            );
          }
          return false;
        } finally {
          screenLoading.close();
          screenLoading = null;
        }

        if (!this.isCurrentMediaRequest(requestId)) {
          return false;
        }

        const screenTrack = screenStream.getVideoTracks()[0];
        if (!screenTrack) {
          this.$message.error("The screen-sharing stream is unavailable.");
          return false;
        }
        if (screenTrack.getSettings().displaySurface !== "monitor") {
          this.$alert(
            "Please share your entire screen.",
            "Screen Sharing Failed",
            {
              confirmButtonText: "OK",
              type: "error",
            }
          ).catch(() => {});
          return false;
        }
        await this.playCaptureVideo(screenVideo, screenStream);

        if (this.screenOnly) {
          if (
            !this.isCurrentMediaRequest(requestId) ||
            screenTrack.readyState !== "live"
          ) {
            return false;
          }
          this.screenVideo = screenVideo;
          this.screenStream = screenStream;
          committed = true;
          this.bindTrackEnded(screenTrack, "screen", requestId);
          this.bindWindowActivityHandlers();
          this.status = true;
          this.$emit("input", true);
          this.startShot();
          return true;
        }

        cameraLoading = this.$loading({
          lock: true,
          text: "Getting camera authorization...",
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });
        try {
          cameraStream = await navigator.mediaDevices.getUserMedia(
            this.cameraOptions
          );
        } catch (error) {
          if (this.isCurrentMediaRequest(requestId) && !automatic) {
            this.$message.error(
              this.getMediaRequestErrorMessage(error, "camera")
            );
          }
          return false;
        } finally {
          cameraLoading.close();
          cameraLoading = null;
        }

        if (!this.isCurrentMediaRequest(requestId)) {
          return false;
        }

        const cameraTrack = cameraStream.getVideoTracks()[0];
        if (!cameraTrack) {
          this.$message.error("The camera stream is unavailable.");
          return false;
        }
        await this.playCaptureVideo(cameraVideo, cameraStream);
        if (
          !this.isCurrentMediaRequest(requestId) ||
          cameraTrack.readyState !== "live" ||
          screenTrack.readyState !== "live"
        ) {
          return false;
        }

        this.cameraVideo = cameraVideo;
        this.screenVideo = screenVideo;
        this.cameraStream = cameraStream;
        this.screenStream = screenStream;
        committed = true;

        this.bindTrackEnded(cameraTrack, "camera", requestId);
        this.bindTrackEnded(screenTrack, "screen", requestId);
        this.bindWindowActivityHandlers();
        this.status = true;
        this.$emit("input", true);
        this.startShot();
        return true;
      } catch (error) {
        if (this.isCurrentMediaRequest(requestId)) {
          console.error("Unable to initialize exam proctoring", error);
          if (!automatic) {
            this.$message.error(`Failed to start proctoring. Error: ${error}`);
          }
        }
        return false;
      } finally {
        if (cameraLoading) {
          cameraLoading.close();
        }
        if (screenLoading) {
          screenLoading.close();
        }
        if (!committed) {
          this.stopMediaStream(cameraStream);
          this.stopMediaStream(screenStream);
          this.detachCaptureVideo(cameraVideo);
          this.detachCaptureVideo(screenVideo);
        }
        if (this.isCurrentMediaRequest(requestId)) {
          this.initializing = false;
        }
      }
    },
    createCaptureVideo() {
      const video = document.createElement("video");
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      return video;
    },
    playCaptureVideo(video, stream) {
      video.srcObject = stream;
      const playPromise = video.play();
      return playPromise && typeof playPromise.then === "function"
        ? playPromise
        : Promise.resolve();
    },
    detachCaptureVideo(video) {
      if (video) {
        video.srcObject = null;
      }
    },
    stopMediaStream(stream) {
      if (stream && stream.getTracks) {
        stream.getTracks().forEach((track) => track.stop());
      }
    },
    isCurrentMediaRequest(requestId) {
      return !this.isDestroyed && requestId === this.mediaRequestId;
    },
    bindTrackEnded(track, streamType, requestId) {
      track.addEventListener("ended", () => {
        if (!this.isCurrentMediaRequest(requestId)) {
          return;
        }
        console.warn(`Exam proctoring ${streamType} track ended`);
        this.clearAllResource();
      }, { once: true });
    },
    bindWindowActivityHandlers() {
      if (this.windowActivityHandlersBound) {
        return;
      }
      window.addEventListener("blur", this.onblur);
      window.addEventListener("focus", this.onfocus);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.windowActivityHandlersBound = true;
    },
    unbindWindowActivityHandlers() {
      if (!this.windowActivityHandlersBound) {
        return;
      }
      window.removeEventListener("blur", this.onblur);
      window.removeEventListener("focus", this.onfocus);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.windowActivityHandlersBound = false;
    },
    async getShot(isChange) {
      // camera or screen
      if (!this.status) {
        return;
      }

      let camera_pic = "";
      let screen_pic = "";
      if (this.cameraVideo) {
        let canvas = this.$refs.cameraCanvas;
        let ctx = canvas.getContext("2d");
        ctx.drawImage(
          this.cameraVideo,
          0,
          0,
          this.cameraOptions.video.width,
          this.cameraOptions.video.height
        );
        camera_pic = canvas.toDataURL(
          SHOT_MIME_TYPE,
          CAMERA_SHOT_QUALITY
        );
      }
      if (this.screenVideo) {
        let canvas = this.$refs.screenCanvas;
        let ctx = canvas.getContext("2d");
        ctx.drawImage(
          this.screenVideo,
          0,
          0,
          this.screenOptions.video.width,
          this.screenOptions.video.height
        );
        screen_pic = canvas.toDataURL(
          SHOT_MIME_TYPE,
          SCREEN_SHOT_QUALITY
        );
      }

      const uploadTasks = [];
      if (camera_pic) {
        const cameraFile = this.base64ToFile(camera_pic, "camera_pic.jpg");
        uploadTasks.push(
          this.uploadEvidenceFile("camera", cameraFile).then((url) => ({
            name: "camera_pic",
            url,
          }))
        );
      }
      if (screen_pic) {
        const screenFile = this.base64ToFile(screen_pic, "screen_pic.jpg");
        uploadTasks.push(
          this.uploadEvidenceFile("screen", screenFile).then((url) => ({
            name: "screen_pic",
            url,
          }))
        );
      }
      if (!uploadTasks.length) {
        return;
      }

      try {
        const urlList = await Promise.all(uploadTasks);
        const params = {
          student_id: getUserId(),
          project_code: this.resolvedProjectCode,
          template_id: this.template_id,
          url_list: urlList,
        };
        if (isChange) {
          params.isChange = true;
        }
        await uploadExamShotByUrl(params);
      } catch (error) {
        console.error("Exam evidence screenshot upload failed", error);
      }
    },
    async uploadEvidenceFile(evidenceType, file) {
      const tokenResponse = await getExamEvidenceQiniuToken(
        evidenceType,
        this.resolvedProjectCode,
        this.template_id,
        getUserId()
      );
      const responseData = tokenResponse && tokenResponse.data;
      const tokenData = responseData && responseData.data;
      if (
        !responseData ||
        responseData.code !== 0 ||
        !tokenData ||
        !tokenData.token ||
        !tokenData.key_prefix
      ) {
        throw new Error(`Failed to get ${evidenceType} upload token`);
      }

      const uniqueSuffix = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const objectKey = tokenData.key_prefix + uniqueSuffix + ".jpg";
      const uploadResult = await new Promise((resolve, reject) => {
        qiniu.upload(file, objectKey, tokenData.token).subscribe({
          error: reject,
          complete: resolve,
        });
      });
      return EXAM_EVIDENCE_BASE_URL + uploadResult.key;
    },
    clearAllResource() {
      const hadActiveResource = Boolean(
        this.status || this.cameraStream || this.screenStream
      );
      this.clearShotTimer();
      this.clearScreenVisibleShot();
      this.unbindWindowActivityHandlers();
      this.stopMediaStream(this.cameraStream);
      this.stopMediaStream(this.screenStream);
      this.detachCaptureVideo(this.cameraVideo);
      this.detachCaptureVideo(this.screenVideo);
      this.cameraVideo = null;
      this.screenVideo = null;
      this.cameraStream = null;
      this.screenStream = null;
      this.pageInactive = false;
      this.status = false;
      if (hadActiveResource) {
        this.$emit("input", false);
      }
    },
    isVideoStreamLive(stream) {
      return Boolean(
        stream &&
        stream.getVideoTracks &&
        stream.getVideoTracks().some((track) => track.readyState === "live")
      );
    },
    startCheckOnline() {
      this.timer = setInterval(() => {
        if (this.isDestroyed || this.initializing || !this.status) {
          return;
        }
        if (
          (!this.screenOnly && !this.isVideoStreamLive(this.cameraStream)) ||
          !this.isVideoStreamLive(this.screenStream)
        ) {
          console.warn("Exam proctoring stream is no longer live");
          this.clearAllResource();
        }
      }, 1000);
    },
    startShot() {
      this.clearShotTimer();
      const generation = this.shotGeneration;
      initExamShotTimePoint(
        getUserId(),
        this.resolvedProjectCode,
        this.template_id
      ).then((res) => {
        if (
          this.isDestroyed ||
          !this.status ||
          generation !== this.shotGeneration
        ) {
          return;
        }
        this.index = 0;
        this.shotTable = this.changeShotTable(res.data);
        this.shotByTable(generation);
      }).catch((error) => {
        console.error("Unable to initialize exam evidence schedule", error);
      });
    },
    shotByTable(generation) {
      if (
        generation !== this.shotGeneration ||
        !this.status ||
        this.index >= this.shotTable.length
      ) {
        return;
      }

      const delaySeconds = this.shotTable[this.index];
      this.shotTimer = setTimeout(() => {
        this.shotTimer = null;
        if (
          generation !== this.shotGeneration ||
          !this.status ||
          this.isDestroyed
        ) {
          return;
        }
        this.getShot();
        this.index++;
        this.shotByTable(generation);
      }, delaySeconds * 1000);
    },
    clearShotTimer() {
      this.shotGeneration++;
      if (this.shotTimer !== null) {
        clearTimeout(this.shotTimer);
        this.shotTimer = null;
      }
    },
    setScreenVisibleShot() {
      this.clearScreenVisibleShot();
      if (!this.status) {
        return;
      }
      this.visibleShotTimer = setInterval(() => {
        this.getShot(true);
      }, 20000);
    },
    clearScreenVisibleShot() {
      if (this.visibleShotTimer) {
        clearInterval(this.visibleShotTimer);
        this.visibleShotTimer = "";
      }
    },
    clearCheckOnline() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = "";
      }
    },
    changeShotTable(table) {
      if (!Array.isArray(table) || table.length === 0) {
        return [];
      }

      const delays = [0];
      for (let index = 1; index < table.length; index++) {
        const delay = Number(table[index]) - Number(table[index - 1]);
        delays.push(Number.isFinite(delay) && delay >= 0 ? delay : 0);
      }
      return delays;
    },
    base64ToFile(base64, name) {
      if (base64) {
        let dataurl = base64;
        let arr = dataurl.split(",");
        let mine = arr[0].match(/:(.*?);/)[1];
        let bstr = atob(arr[1]);
        let n = bstr.length;
        let u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        let obj = new File([u8arr], name, { type: mine });
        return obj;
      } else {
        return "";
      }
    },
  },
};
</script>

<style scoped>
.invigilate-monitor {
  width: 100%;
  display: flex;
  align-items: flex-start;
}

.tag {
  cursor: pointer;
  margin-bottom: 2px;
  width: 100%;
  height: 40px;
  line-height: 40px;
  text-align: center;
  /* padding: 2px 10px; */
  color: #BE3739;
  /* border: 2px solid #11448D; */
  background-color: #EFB93F;

}
</style>
